import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import Gyanbeej from '../media/LOGO Gyanbeej transparent.png';
import GyanSparkLogoSvg from '../media/gyanspark-logo-dark-horizontal.svg';
import productsImg from '../media/generated/products-header.webp';
import { Section, SectionHeading, PageHero, Faq, CtaBand, fadeUp } from '../components/ui';
import { ArrowUpRightIcon, CheckIcon } from '../components/icons';

const PRODUCTS = [
  {
    logo: GyanSparkLogoSvg,
    logoClass: 'h-8',
    name: 'GyanSpark',
    eyebrow: 'For EdTech platforms',
    tagline: 'Your platform should know every student',
    description:
      "GyanSpark gives every feature you've built one shared understanding of each learner. It sits between your existing AI tools and your students, turning scattered interactions into one continuous profile. Unlike Gyanbeej, it isn't tied to any specific curriculum — it personalizes whatever subject or syllabus your platform already teaches.",
    features: [
      'Not limited to any specific curriculum or syllabus',
      'Unified learner profile across every tool you run',
      'Non-intrusive — no new hosting infrastructure',
      'Works with OpenAI, Claude, and Gemini',
      'Tracks recurring gaps across sessions and tools',
      'Two ways in: wrap existing AI, or start from your content',
    ],
    to: '/products/gyanspark',
    site: 'https://www.gyanspark.com/',
    accent: { text: 'text-amber-400', bg: 'bg-amber-500 hover:bg-amber-600', chip: 'bg-amber-500/10 text-amber-300 border-amber-400/20' },
  },
  {
    logo: Gyanbeej,
    logoClass: 'h-10',
    name: 'Gyanbeej',
    eyebrow: 'For students, teachers & parents',
    tagline: 'Your AI learning assistant for NCERT textbooks',
    description:
      'Built specifically for students who follow the NCERT curriculum, Class 6 to 10, covering Science, Social Science, and Mathematics. Students ask questions in their own words and get explanations shaped to how they learn — through chat, stories, quizzes, or voice.',
    features: [
      'Strictly aligned to the NCERT curriculum, Class 6–10',
      'Covers Science, Social Science, and Mathematics',
      'AI-powered interactive doubt-solving, 24/7',
      'Story Cards that explain concepts as narratives',
      'Quiz Mode and Rapid Fire for exam practice',
      'Voice Mode for hands-free conversational learning',
    ],
    to: '/products/gyanbeej',
    site: 'https://www.gyanbeej.com/',
    accent: { text: 'text-purple-400', bg: 'bg-purple-600 hover:bg-purple-700', chip: 'bg-purple-500/10 text-purple-300 border-purple-400/20' },
  },
];

const ProductCards = () => (
  <Section tone="raised">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
      {PRODUCTS.map((p, i) => (
        <motion.div
          key={p.name}
          {...fadeUp}
          transition={{ duration: 0.5, delay: i * 0.1 }}
          className="bg-white/[0.04] rounded-3xl border border-white/10 shadow-lg p-8 md:p-10 flex flex-col hover:shadow-2xl transition-shadow"
        >
          <span className={`text-xs font-bold tracking-widest uppercase mb-5 ${p.accent.text}`}>{p.eyebrow}</span>
          <img src={p.logo} alt={`${p.name} logo`} className={`${p.logoClass} object-contain self-start mb-6`} />
          <h2 className="text-xl font-extrabold text-white mb-3">{p.tagline}</h2>
          <p className="text-gray-400 leading-relaxed mb-6">{p.description}</p>
          <ul className="space-y-2.5 mb-8">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-gray-200">
                <CheckIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.accent.text}`} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-auto flex flex-col sm:flex-row gap-3">
            <Link to={p.to} className={`flex-1 text-center px-6 py-3 rounded-full text-white font-semibold transition-colors ${p.accent.bg}`}>
              Learn more
            </Link>
            <a
              href={p.site}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-full border border-white/10 font-semibold text-gray-200 hover:bg-white/[0.04] transition-colors"
            >
              Visit site <ArrowUpRightIcon className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      ))}
    </div>
  </Section>
);

const ROWS = [
  ['Who it’s for', 'Educational platforms & LMS', 'Students, teachers, parents'],
  ['What it is', 'Middleware you integrate', 'A learning app you use directly'],
  ['Curriculum', 'Not limited to any specific curriculum', 'Strictly NCERT, Class 6–10'],
  ['Coverage', 'Any subject your platform already covers', 'Science, Social Science, Mathematics'],
  ['Core benefit', 'Personalize every feature of your educational platform', 'Understand NCERT concepts faster'],
  ['Setup', 'Integrate within your educational platform', 'Sign up and start asking'],
  ['Get started', 'Book a demo', 'Free account'],
];

const Comparison = () => (
  <Section>
    <SectionHeading
      eyebrow="Compare"
      title="Which one is for you?"
      subtitle="If you're studying or teaching, start with Gyanbeej. If you're building an education platform, start with GyanSpark."
    />
    <motion.div {...fadeUp} className="max-w-4xl mx-auto overflow-x-auto">
      <table className="w-full border-collapse min-w-[600px]">
        <thead>
          <tr>
            <th className="w-1/4" />
            <th className="p-4 text-left">
              <img src={GyanSparkLogoSvg} alt="GyanSpark" className="h-6 object-contain mb-1" />
              <span className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Platform-facing</span>
            </th>
            <th className="p-4 text-left">
              <img src={Gyanbeej} alt="Gyanbeej" className="h-7 object-contain mb-1" />
              <span className="text-xs font-semibold text-purple-400 uppercase tracking-wide">Student-facing</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, a, b], i) => (
            <tr key={label} className={i % 2 === 0 ? 'bg-white/[0.04]' : ''}>
              <td className="p-4 text-sm font-bold text-white rounded-l-xl">{label}</td>
              <td className="p-4 text-sm text-gray-400">{a}</td>
              <td className="p-4 text-sm text-gray-400 rounded-r-xl">{b}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  </Section>
);

const ProductsFaq = () => (
  <Section tone="raised">
    <SectionHeading eyebrow="FAQ" title="Questions about our products" />
    <Faq
      items={[
        {
          q: 'Can I use Gyanbeej and GyanSpark together?',
          a: 'They serve different audiences, so most people use one or the other. Gyanbeej is for individual learners and educators; GyanSpark is for companies building their own education software. They share the same underlying belief in learner-aware personalization.',
        },
        {
          q: 'Is GyanSpark limited to the NCERT curriculum like Gyanbeej?',
          a: "No. Gyanbeej is built specifically for students following the NCERT curriculum, Class 6 to 10. GyanSpark is curriculum-agnostic — it personalizes whatever subject or syllabus your platform already teaches, not just NCERT.",
        },
        {
          q: 'Which classes and subjects does Gyanbeej cover?',
          a: 'Gyanbeej is for students who follow NCERT textbooks from Class 6 to Class 10, covering Science (Physics, Chemistry, Biology), Social Science (History, Geography, Civics), and Mathematics (Algebra, Geometry, Statistics).',
        },
        {
          q: 'Which AI providers does GyanSpark support?',
          a: 'GyanSpark works with OpenAI, Claude, and Gemini. If you already run AI tools on one of these providers, GyanSpark can wrap what you have rather than replacing it.',
        },
        {
          q: 'What if my platform has no AI features yet?',
          a: 'That is one of the two supported paths. If you have no AI yet, GyanSpark can start from your own content instead of wrapping existing tools.',
        },
        {
          q: 'How do I try either product?',
          a: 'Gyanbeej has a free sign-up on gyanbeej.com. For GyanSpark, book a demo on gyanspark.com and the team will walk through integration options for your platform.',
        },
      ]}
    />
  </Section>
);

const Products = () => (
  <>
    <Seo
      title="Products"
      description="Explore Gyanbeej, an AI learning assistant for NCERT students Class 6-10, and GyanSpark, a personalization layer for EdTech platforms — both built by Preative Solutions."
      path="/products"
    />
    <PageHero
      eyebrow="Our products"
      title="Two products, one mission"
      subtitle="One helps students learn directly. The other helps education platforms understand the students they already serve."
      image={productsImg}
      imageAlt="Abstract illustration of two connected software products"
    />
    <ProductCards />
    <Comparison />
    <ProductsFaq />
    <CtaBand title="Not sure which fits?" subtitle="Tell us about your situation and we'll point you to the right one.">
      <Link to="/contact" className="px-7 py-3 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-purple-500/10 transition-colors">
        Talk to us
      </Link>
    </CtaBand>
  </>
);

export default Products;
